import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { apiFetch } from '@shared/api/http'
import type { DashboardDateRange } from '../lib/admin-dashboard-period'

const RecuperaAdminOverviewSchema = z.object({
  recoveredCount: z.number(),
  recoveredRevenue: z.string(),
  commission: z.string(),
})

const RecuperaAdminBreakdownItemSchema = z.object({
  producerId: z.string(),
  producerEmail: z.string().optional(),
  productId: z.string().optional(),
  productName: z.string().optional(),
  recoveredCount: z.number(),
  recoveredRevenue: z.string(),
  commission: z.string(),
})

const RecuperaAdminBreakdownSchema = z.object({
  items: z.array(RecuperaAdminBreakdownItemSchema),
  nextCursor: z.string().optional(),
})

const RecuperaAdminProductSchema = z.object({
  productId: z.string(),
  productName: z.string().optional(),
})

const RecuperaAdminProductsSchema = z.array(RecuperaAdminProductSchema)

export type RecuperaAdminOverview = z.infer<typeof RecuperaAdminOverviewSchema>
export type RecuperaAdminBreakdownItem = z.infer<typeof RecuperaAdminBreakdownItemSchema>
export type RecuperaAdminProduct = z.infer<typeof RecuperaAdminProductSchema>

export interface RecuperaAdminFilter extends DashboardDateRange {
  producerId?: string
  productId?: string
  search?: string
}

function filterSearch(filter: RecuperaAdminFilter): URLSearchParams {
  const search = new URLSearchParams()
  if (filter.dateFrom !== undefined) search.set('dateFrom', filter.dateFrom)
  if (filter.dateTo !== undefined) search.set('dateTo', filter.dateTo)
  if (filter.producerId !== undefined) search.set('producerId', filter.producerId)
  if (filter.productId !== undefined) search.set('productId', filter.productId)
  if (filter.search !== undefined && filter.search !== '') search.set('search', filter.search)
  return search
}

function querySuffix(params: URLSearchParams): string {
  const query = params.toString()
  return query === '' ? '' : `?${query}`
}

export function useRecuperaAdminOverview(filter: RecuperaAdminFilter) {
  return useQuery({
    queryKey: ['admin-recupera-overview', filter],
    queryFn: async ({ signal }) =>
      RecuperaAdminOverviewSchema.parse(
        await apiFetch<unknown>(`/admin/recupera/overview${querySuffix(filterSearch(filter))}`, {
          signal,
        }),
      ),
    staleTime: 30_000,
  })
}

export function useRecuperaAdminProducts(filter: RecuperaAdminFilter) {
  return useQuery({
    queryKey: [
      'admin-recupera-products',
      filter.dateFrom,
      filter.dateTo,
      filter.producerId,
      filter.search,
    ],
    queryFn: async ({ signal }) =>
      RecuperaAdminProductsSchema.parse(
        await apiFetch<unknown>(`/admin/recupera/products${querySuffix(filterSearch(filter))}`, {
          signal,
        }),
      ),
    staleTime: 30_000,
  })
}

export function useRecuperaAdminBreakdown(filter: RecuperaAdminFilter) {
  return useInfiniteQuery({
    queryKey: ['admin-recupera-breakdown', filter],
    queryFn: async ({ pageParam, signal }) => {
      const params = filterSearch(filter)
      if (pageParam) params.set('cursor', pageParam)
      return RecuperaAdminBreakdownSchema.parse(
        await apiFetch<unknown>(`/admin/recupera/breakdown${querySuffix(params)}`, { signal }),
      )
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 30_000,
  })
}
